import { CACHE_CONFIG_TYPES, STORE_NAME } from '@intigral/constant';
import { DI } from '@intigral/constant/config/DIContainer';
import { container } from '@intigral/domain/lib/DataDomain';
import { IMpxService } from '@intigral/domain/lib/application/services/MpxService';
import { IThinkAnalyticsService } from '@intigral/domain/lib/application/services/ThinkAnalyticsService';
import { IUserRegistrationService } from '@intigral/domain/lib/application/services/UserRegistrationService';
import { IApiPathManager } from '@intigral/domain/lib/infrastructures/config/ApiPathManager';
import { Consent } from '@intigral/ui';
import { convertHttpToHttps, log } from '@intigral/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { appConfig } from '../../config/app.config';
import { EVENT } from '../../contexts/MixpanelConstants';
import { MixpanelContext } from '../../contexts/MixpanelContext';
import h, { getCountry } from '../../helpers/common';
import user from '../../helpers/user';
import { useProfileSwitching } from '../../hooks/useProfileSwitching';
import { urlAfterProfileSelect } from '../../utils/pendingDeepLink';
import { IStore } from '../../interfaces/StoreTypes';
import { getUserInfo } from '../../utils/user';
import { useConfigurationSections } from '../../hooks/useConfiguration';
import { ConsentFooter, Loader, MandatoryConsent, OptionalConsent } from '../elements/consent';
import { useConfiguration } from '../../hooks/useConfiguration';

type FeedItem = {
    GUID: string;
    'VOD Thumbnail': string;
};

type IMixpanelContext = {
    track?: (name: string, value: any) => void;
};

export const ConsentManagement = () => {
    const [step, setStep] = useState(0);
    const [userConsents, setUserConsents] = useState<any>({ mandatory: {}, optional: {} });
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    // Using React Query instead of Redux for configuration
    const { consentManagement, layoutConfig } = useConfigurationSections();
    const { consentQuestions, consentVersion } = consentManagement || {};
    const { carousels } = layoutConfig?.optionalConsent || {};
    const feedDataSource = carousels?.[0]?.dataSources?.[0] || 'marketing_cmt_action';
    const feedType = carousels?.[0]?.feedType || 'ta';
    const selectedProfile = useSelector((store: IStore) => store.profile);

    const geolocation = useSelector((store: IStore) => store.location);
    const mandatoryQuestions = useMemo(
        () => (Array.isArray(consentQuestions) ? consentQuestions.filter((q) => q.isMandatory) : []),
        [consentQuestions]
    );
    const optionalQuestions = useMemo(
        () => (Array.isArray(consentQuestions) ? consentQuestions.filter((q) => !q.isMandatory) : []),
        [consentQuestions]
    );

    const [isMandatoryComplete, setIsMandatoryComplete] = useState(false);

    const { track } = useContext(MixpanelContext) as IMixpanelContext;
    const queryClient = useQueryClient();

    const { urlToOpenTheProfile: targetUrl } = useProfileSwitching(user.getProfile());

    const handleToggleConsent = (questionId: string, isMandatory: boolean, value: boolean) => {
        if (isMandatory) {
            // mandatory
            track(EVENT.CONSENT.CONSENT_TC_PROMPT, { value });
        } else {
            track(EVENT.CONSENT.CONSENT_MARKETING_PROMPT, { value });
        }
        setUserConsents((prevConsents) => ({
            ...prevConsents,
            [isMandatory ? 'mandatory' : 'optional']: {
                ...prevConsents[isMandatory ? 'mandatory' : 'optional'],
                [questionId]: value,
            },
        }));
    };

    const { mutateAsync: submitConsent, isPending } = useMutation({
        mutationFn: async () => {
            const { apiKey, identityToken, authToken } = h.getStore(STORE_NAME.USER) || {};
            const consentPayload = {
                consentVersion: consentVersion,
                consents: userConsents,
            };
            return container
                .resolve<IUserRegistrationService>(DI.SERVICES.IUserRegistrationService)
                .addConsent(apiKey, consentPayload, authToken, identityToken);
        },
        onSuccess: () => {
            queryClient.setQueryData(['profiles', 'show-consent'], { consentNeeded: false });
            setTimeout(() => {
                navigate(urlAfterProfileSelect(targetUrl));
            }, 200);
        },
    });

    const handleNext = () => {
        // Look for the next unacknowledged mandatory question
        const nextUnacknowledgedMandatory = mandatoryQuestions.find(
            (q, index) => !userConsents.mandatory[q.id] && index > step
        );

        // Look for the next unacknowledged optional question
        const nextUnacknowledgedOptional = optionalQuestions.find(
            (q, index) => !userConsents.optional[q.id] && index > step
        );

        if (!isMandatoryComplete) {
            track(EVENT.CONSENT.CONSENT_TC_NEXT, {});
            // Move to the next unacknowledged mandatory question
            if (nextUnacknowledgedMandatory) {
                setStep(mandatoryQuestions.indexOf(nextUnacknowledgedMandatory));
            } else {
                // All mandatory questions have been completed
                setIsMandatoryComplete(true);
                setStep(0); // Reset step for optional questions
            }
        } else {
            track(EVENT.CONSENT.CONSENT_MARKETING_NEXT, {});
            // Move to the next unacknowledged optional question
            if (nextUnacknowledgedOptional) {
                setStep(optionalQuestions.indexOf(nextUnacknowledgedOptional));
            } else {
                // All optional questions have been answered, submit the consents
                //submitUserConsent();
                submitConsent();
            }
        }
    };

    const moveToNextUnacknowledgedQuestion = (consents) => {
        // Find the first unacknowledged mandatory question
        const nextUnacknowledgedMandatory = mandatoryQuestions.find((q) => !consents.mandatory[q.id]);

        if (nextUnacknowledgedMandatory) {
            setStep(mandatoryQuestions.indexOf(nextUnacknowledgedMandatory));
        } else {
            // If all mandatory questions are acknowledged, mark them complete and move to optional
            setIsMandatoryComplete(true);

            // Find the first unacknowledged optional question
            const nextUnacknowledgedOptional = optionalQuestions.find((q) => !consents.optional[q.id]);

            if (nextUnacknowledgedOptional) {
                setStep(optionalQuestions.indexOf(nextUnacknowledgedOptional));
            } else {
                // If all optional questions are acknowledged too, redirect user to the home screen
                navigate(urlAfterProfileSelect(targetUrl));
            }
        }
        setIsLoading(false);
    };

    const getImage = (item) => {
        return item.thumbnails['portrait-highestResolution'] ? item.thumbnails['portrait-highestResolution'].url : '';
    };

    const { data: config } = useConfiguration();

    const getFeedItems = async (): Promise<FeedItem[]> => {
        try {
            let feedItems: FeedItem[] = [];
            if (feedType === 'mpx') {
                try {
                    const apiPathManager: IApiPathManager = container.resolve<IApiPathManager>(
                        DI.UTILS.IApiPathManager
                    );
                    const url = apiPathManager.getByDatasource(feedDataSource);
                    const mpxService = container.resolve<IMpxService>(DI.SERVICES.IMpxService);
                    const feedResponse = await mpxService.getFeed(url, {
                        byCustomValue: `{PC}{${getCountry()}}`,
                    });
                    const data = feedResponse?.entries || [];
                    if (data?.length) {
                        feedItems = data.map((item) => ({
                            GUID: item.guid,
                            'VOD Thumbnail': h.getImagizerUrl(
                                convertHttpToHttps(getImage(item)),
                                config?.imagizer?.components_images.VOD_ITEM
                            ),
                        }));
                    } else {
                        return [];
                    }
                } catch (error) {
                    return [];
                }
            } else if (feedType === 'ta') {
                try {
                    const userInfo = getUserInfo();
                    const params: Record<string, string | string[]> = {
                        userName: userInfo?.username,
                        device: 'PC',
                        profileName: selectedProfile.selectedProfile.profileName,
                        country: geolocation.countryCode,
                        rating: selectedProfile.selectedProfile.parentalControl,
                        subGuids: userInfo?.subscriptionGUID,
                    };
                    const taService = container.resolve<IThinkAnalyticsService>(DI.SERVICES.IThinkAnalyticsService);
                    const feedResponse = await taService.getTAByUsecaseId(
                        feedDataSource,
                        userInfo?.apiKey,
                        userInfo?.identityToken,
                        params
                    );

                    const data = feedResponse?.data?.entries || [];
                    if (data?.length) {
                        feedItems = data.map((item) => ({
                            GUID: item.guid,
                            'VOD Thumbnail': h.getImagizerUrl(
                                convertHttpToHttps(getImage(item)),
                                config?.imagizer.components_images.VOD_ITEM
                            ),
                        }));
                    } else {
                        return [];
                    }
                } catch (error) {
                    return [];
                }
            }

            return feedItems;
        } catch (error) {
            return [];
        }
    };

    const { data: feedData } = useQuery({
        queryKey: [
            'consent-optional-feed',
            feedType,
            feedDataSource,
            geolocation?.countryCode,
            selectedProfile?.selectedProfile?.profileName,
        ],
        queryFn: getFeedItems,
        enabled: isMandatoryComplete && !!config,
        staleTime: 5 * 60 * 1000,
    });

    useEffect(() => {
        const getUserConsent = async () => {
            setIsLoading(true);
            try {
                const { apiKey, identityToken, authToken } = h.getStore(STORE_NAME.USER) || {};
                if (!apiKey || !authToken || !identityToken) {
                    return;
                }
                const { consents, consentVersion: storedConsentVersion } = await container
                    .resolve<IUserRegistrationService>(DI.SERVICES.IUserRegistrationService)
                    .getConsent(apiKey, authToken, identityToken);
                if (consentVersion > storedConsentVersion) return;

                if (Object.keys(consents).length > 0) {
                    setUserConsents((prevConsents) => ({
                        mandatory: {
                            ...prevConsents.mandatory,
                            ...consents.mandatory,
                        },
                        optional: {
                            ...prevConsents.optional,
                            ...consents.optional,
                        },
                    }));
                    moveToNextUnacknowledgedQuestion(consents);
                }
            } catch (error) {
                log.error('[Consent Management] Error fetching user consents:', error);
            } finally {
                setIsLoading(false);
            }
        };
        getUserConsent();
    }, []);

    useEffect(() => {
        const initializeConsents = () => {
            const initialMandatoryConsents = mandatoryQuestions.reduce((acc, question) => {
                acc[question.id] = false;
                return acc;
            }, {});

            const initialOptionalConsents = optionalQuestions.reduce((acc, question) => {
                acc[question.id] = false;
                return acc;
            }, {});

            setUserConsents({ mandatory: initialMandatoryConsents, optional: initialOptionalConsents });
        };

        initializeConsents();
    }, [mandatoryQuestions, optionalQuestions]);

    const currentQuestion = isMandatoryComplete ? optionalQuestions[step] : mandatoryQuestions[step];
    const isMandatory = !isMandatoryComplete;

    return (
        <Consent
            data-test-id="consent-management-container"
            title=""
            variant={isMandatory ? 'full' : 'fit'}
            footer={
                <ConsentFooter
                    onToggle={handleToggleConsent}
                    onSubmit={handleNext}
                    question={currentQuestion}
                    isChecked={userConsents?.[isMandatory ? 'mandatory' : 'optional']?.[currentQuestion?.id] || false}
                />
            }
        >
            {isLoading || isPending ? (
                <Loader />
            ) : isMandatory ? (
                <MandatoryConsent question={currentQuestion} />
            ) : (
                <OptionalConsent data={feedData ?? null} question={currentQuestion} />
            )}
        </Consent>
    );
};
